'use client'

import { useState, useEffect, useMemo, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'

interface Analysis {
  id: string
  pdfFilename: string
  processingStatus: string
  promptVersion: string
  createdAt: string
  _count?: { forms: number }
}

type DocumentType = 'cgl' | 'coi' | 'subcontract'

const TYPE_CONFIG: Record<DocumentType, { label: string; pluralLabel: string }> = {
  cgl: { label: 'CGL Policy', pluralLabel: 'CGL Policies' },
  coi: { label: 'Certificate (COI)', pluralLabel: 'Certificates (COI)' },
  subcontract: { label: 'Subcontract', pluralLabel: 'Subcontracts' }
}

const getTypeFromPromptVersion = (promptVersion: string): DocumentType => {
  if (promptVersion === 'coiAnalysisPrompt') return 'coi'
  if (promptVersion === 'glPolicyPrompt_GCClient') return 'cgl'
  if (promptVersion === 'subcontractPrompt') return 'subcontract'
  return 'cgl' // fallback
}

const getStatusBadge = (status: string) => {
  const styles: Record<string, string> = {
    completed: 'bg-green-100 text-green-800',
    processing: 'bg-yellow-100 text-yellow-800 animate-pulse',
    pending: 'bg-gray-100 text-gray-800',
    failed: 'bg-red-100 text-red-800'
  }
  return styles[status] || 'bg-gray-100 text-gray-800'
}

function HistoryPageContent() {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [analyses, setAnalyses] = useState<Analysis[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<DocumentType>>(new Set())
  const [comparing, setComparing] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Selection state from URL
  const selectedIdsParam = searchParams.get('selected')
  const selectedIds = useMemo(() =>
    selectedIdsParam ? new Set(selectedIdsParam.split(',')) : new Set<string>(),
    [selectedIdsParam]
  )
  const isSelectionMode = searchParams.get('mode') === 'select'

  // Fetch analyses
  useEffect(() => {
    fetch('/api/analyses')
      .then(res => res.json())
      .then(data => {
        if (data.error) throw new Error(data.error)
        setAnalyses(data)
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  // Group analyses by type
  const groupedAnalyses = useMemo(() => {
    const groups: Record<DocumentType, Analysis[]> = {
      cgl: [],
      coi: [],
      subcontract: []
    }
    analyses.forEach(a => {
      const type = getTypeFromPromptVersion(a.promptVersion)
      groups[type].push(a)
    })
    return groups
  }, [analyses])

  // Selection helpers
  const updateSelection = (newSelectedIds: Set<string>) => {
    const params = new URLSearchParams(searchParams.toString())
    if (newSelectedIds.size > 0) {
      params.set('selected', Array.from(newSelectedIds).join(','))
      params.set('mode', 'select')
    } else {
      params.delete('selected')
      params.delete('mode')
    }
    router.push(`/analyses?${params.toString()}`, { scroll: false })
  }

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    updateSelection(newSelected)
  }

  const enterSelectionMode = () => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('mode', 'select')
    router.push(`/analyses?${params.toString()}`, { scroll: false })
  }

  const exitSelectionMode = () => {
    router.push('/analyses', { scroll: false })
  }

  const clearSelection = () => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('selected')
    router.push(`/analyses?${params.toString()}`, { scroll: false })
  }

  // Toggle group collapse
  const toggleGroup = (type: DocumentType) => {
    const newCollapsed = new Set(collapsedGroups)
    if (newCollapsed.has(type)) {
      newCollapsed.delete(type)
    } else {
      newCollapsed.add(type)
    }
    setCollapsedGroups(newCollapsed)
  }

  // Validation logic
  const selectedAnalyses = analyses.filter(a => selectedIds.has(a.id))
  const validationResult = useMemo(() => {
    if (selectedAnalyses.length === 0) {
      return { valid: false, message: 'Select 2 documents to compare', canCompare: false }
    }
    if (selectedAnalyses.length === 1) {
      return { valid: false, message: '1 selected — Select 1 more to compare', canCompare: false }
    }
    if (selectedAnalyses.length > 2) {
      return { valid: false, message: 'Compare requires exactly 2 documents. Deselect one to continue.', canCompare: false }
    }

    // Check both are completed
    const allCompleted = selectedAnalyses.every(a => a.processingStatus === 'completed')
    if (!allCompleted) {
      return { valid: false, message: 'Both documents must be completed', canCompare: false }
    }

    // Check pair type
    const types = selectedAnalyses.map(a => getTypeFromPromptVersion(a.promptVersion))
    if (types[0] === types[1]) {
      return { valid: false, message: `${TYPE_CONFIG[types[0]].label} ↔ ${TYPE_CONFIG[types[1]].label} — same-type comparison not supported`, canCompare: false }
    }

    const pair = types.sort().join('_')
    const supportedPairs = ['cgl_coi', 'cgl_subcontract']
    if (!supportedPairs.includes(pair)) {
      return { valid: false, message: 'This comparison type is not supported yet', canCompare: false }
    }

    const pairLabel = `${TYPE_CONFIG[types[0]].label} ↔ ${TYPE_CONFIG[types[1]].label}`
    return { valid: true, message: `${pairLabel} comparison supported`, canCompare: true }
  }, [selectedAnalyses])

  // Handle compare action
  const handleCompare = async () => {
    if (!validationResult.canCompare || selectedAnalyses.length !== 2) return

    setComparing(true)
    try {
      const res = await fetch('/api/comparisons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis1Id: selectedAnalyses[0].id,
          analysis2Id: selectedAnalyses[1].id
        })
      })

      const data = await res.json()
      if (data.error) throw new Error(data.error)

      // Navigate to comparison page
      router.push(`/comparison/${data.id}`)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create comparison')
      setComparing(false)
    }
  }

  // Handle delete action
  const handleDelete = async () => {
    if (selectedAnalyses.length === 0) return

    const confirmMessage = selectedAnalyses.length === 1
      ? `Delete "${selectedAnalyses[0].pdfFilename}"? This cannot be undone.`
      : `Delete ${selectedAnalyses.length} documents? This cannot be undone.`

    if (!confirm(confirmMessage)) return

    setDeleting(true)
    try {
      // Delete all selected analyses
      const deletePromises = selectedAnalyses.map(a =>
        fetch(`/api/analyses/${a.id}`, { method: 'DELETE' })
      )

      const results = await Promise.all(deletePromises)
      const failed = results.filter(r => !r.ok)

      if (failed.length > 0) {
        throw new Error(`Failed to delete ${failed.length} document(s)`)
      }

      // Remove deleted items from state
      setAnalyses(prev => prev.filter(a => !selectedIds.has(a.id)))
      // Clear selection
      exitSelectionMode()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analyses...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center text-red-600 py-12">
        <p className="text-lg font-semibold mb-2">Error</p>
        <p>{error}</p>
      </div>
    )
  }

  const groupOrder: DocumentType[] = ['cgl', 'coi', 'subcontract']

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-white">Analysis History</h1>
          <div className="flex items-center gap-3">
            {isSelectionMode ? (
              <button
                onClick={exitSelectionMode}
                className="bg-white/10 text-white px-4 py-2 rounded-md hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
            ) : (
              <button
                onClick={enterSelectionMode}
                className="bg-white/10 text-white px-4 py-2 rounded-md hover:bg-white/20 transition-colors"
              >
                Select
              </button>
            )}
            <Link
              href="/"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Upload New
            </Link>
          </div>
        </div>

        {/* Empty State */}
        {analyses.length === 0 ? (
          <div className="text-center py-12 bg-white/10 rounded-lg">
            <p className="text-white/80 mb-4">No analyses yet</p>
            <p className="text-sm text-white/60">
              Get started by uploading a document.
            </p>
          </div>
        ) : (
        <>
          {/* Grouped List */}
          <div className="space-y-4">
            {groupOrder.map(type => {
              const items = groupedAnalyses[type]
              const config = TYPE_CONFIG[type]
              const isCollapsed = collapsedGroups.has(type)
              const selectedInGroup = items.filter(a => selectedIds.has(a.id)).length

              return (
                <div key={type} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden">
                  {/* Group Header (Sticky) */}
                  <button
                    onClick={() => toggleGroup(type)}
                    className="w-full px-4 py-3 bg-white/5 flex items-center justify-between hover:bg-white/10 sticky top-0 z-10"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-white/60">{isCollapsed ? '▶' : '▼'}</span>
                      <span className="font-medium text-white">
                        {config.pluralLabel} ({items.length})
                      </span>
                      {isSelectionMode && selectedInGroup > 0 && (
                        <span className="text-sm text-blue-400">· {selectedInGroup} selected</span>
                      )}
                    </div>
                  </button>

                  {/* Group Content */}
                  {!isCollapsed && (
                    <div className="divide-y divide-white/10">
                      {items.length === 0 ? (
                        <div className="px-4 py-8 text-center text-white/50">
                          No {config.pluralLabel.toLowerCase()} analyzed
                        </div>
                      ) : (
                        items.map(analysis => {
                          const isSelected = selectedIds.has(analysis.id)
                          const isDisabled = analysis.processingStatus !== 'completed'

                          return (
                            <div
                              key={analysis.id}
                              className={`px-4 py-3 flex items-center gap-3 hover:bg-white/5 ${
                                isSelected ? 'bg-blue-500/20' : ''
                              }`}
                            >
                              {/* Checkbox (Selection Mode Only) */}
                              {isSelectionMode && (
                                <div className="flex-shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onChange={() => toggleSelection(analysis.id)}
                                    className={`w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                                      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                    title={isDisabled ? 'Document still processing' : undefined}
                                  />
                                </div>
                              )}

                              {/* Content (Clickable Link) */}
                              <Link
                                href={`/analysis/${analysis.id}`}
                                className="flex-1 min-w-0"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="min-w-0">
                                    <p className="font-medium text-white truncate">
                                      {analysis.pdfFilename}
                                    </p>
                                    <p className="text-sm text-white/60">
                                      {new Date(analysis.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit'
                                      })}
                                      {analysis._count?.forms && analysis._count.forms > 0 && (
                                        <span> · {analysis._count.forms} forms</span>
                                      )}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(analysis.processingStatus)}`}>
                                      {analysis.processingStatus}
                                    </span>
                                    <span className="text-white/40">›</span>
                                  </div>
                                </div>
                              </Link>
                            </div>
                          )
                        })
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Selection Action Bar (Sticky Bottom) */}
          {isSelectionMode && (
            <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-slate-700 via-blue-800 to-slate-800 border-t border-white/20 shadow-lg z-20">
              <div className="max-w-4xl mx-auto px-4 py-3">
                {/* Top row: Selected items and actions */}
                <div className="flex items-center justify-between gap-4">
                  {/* Selected Items */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="text-sm font-medium text-white whitespace-nowrap">
                      {selectedAnalyses.length} selected
                    </span>
                    <div className="flex gap-2 min-w-0">
                      {selectedAnalyses.slice(0, 2).map(a => (
                        <div key={a.id} className="px-2 py-1 bg-white/10 rounded-md text-sm flex items-center gap-1 min-w-0">
                          <span className="truncate max-w-[100px] text-white/90">{a.pdfFilename}</span>
                          <button
                            onClick={() => toggleSelection(a.id)}
                            className="text-white/50 hover:text-white flex-shrink-0"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={clearSelection}
                      className="px-3 py-1.5 text-sm text-white/70 hover:text-white transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={selectedAnalyses.length === 0 || deleting}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        selectedAnalyses.length > 0 && !deleting
                          ? 'bg-red-600 text-white hover:bg-red-500'
                          : 'bg-white/10 text-white/40 cursor-not-allowed'
                      }`}
                    >
                      {deleting ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Deleting...
                        </span>
                      ) : (
                        'Delete'
                      )}
                    </button>
                    <button
                      onClick={handleCompare}
                      disabled={!validationResult.canCompare || comparing}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        validationResult.canCompare && !comparing
                          ? 'bg-blue-500 text-white hover:bg-blue-400'
                          : 'bg-white/10 text-white/40 cursor-not-allowed'
                      }`}
                    >
                      {comparing ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Creating...
                        </span>
                      ) : (
                        'Compare'
                      )}
                    </button>
                  </div>
                </div>

                {/* Bottom row: Validation message */}
                <div className="mt-2 pt-2 border-t border-white/10">
                  <span className={`text-sm ${validationResult.canCompare ? 'text-green-400' : 'text-white/50'}`}>
                    {validationResult.canCompare ? '✓ ' : ''}{validationResult.message}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Padding for Action Bar */}
          {isSelectionMode && <div className="h-24" />}
        </>
        )}
      </div>
    </div>
  )
}

// Loading fallback for Suspense
function HistoryPageLoading() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
        <p className="text-white/70">Loading...</p>
      </div>
    </div>
  )
}

// Main export with Suspense wrapper for useSearchParams
export default function HistoryPage() {
  return (
    <Suspense fallback={<HistoryPageLoading />}>
      <HistoryPageContent />
    </Suspense>
  )
}
